import { Box, Divider } from "@mui/material";
import React, { useMemo } from "react";

import { TooltipModalButton } from "components/common/TooltipModalButton";
import { ConfidenceLineChart } from "features/confidenceSlider/components/ConfidenceLineChart";
import { Typography } from "new-components/typography/Typography";
import { EvaluationResults } from "types/detectors";


function extractMeanAndStdDev(data: EvaluationResults["balanced_system_accuracies"]) {
	const entries = Object.entries(data);
	const meanValues = entries.filter(([key]) => key.includes("__mean"));
	const points = meanValues
		.map(([key, val]) => {
			const match = key.match(/__([\d.]+)__/);
			if (!match) return null;
			return { x: parseFloat(match[1]), y: val };
		})
		.filter((pt) => pt !== null) as { x: number; y: number }[];
	return { points };
}

interface ConfidenceSliderAccuracyChartProps {
	data: EvaluationResults["balanced_system_accuracies"] | Record<string, number>;
	xMin: number;
	xMax: number;
	yTicks?: number[];
	color?: string;
	syncId?: string;
	thresholdValue?: number;
	tooltipTitle?: React.ReactNode | string;
	chartTitle: string;
	chartInfoTooltip: { title: string; content: string };
	showDashedLine?: boolean;
}

export function ConfidenceSliderAccuracyChart(props: ConfidenceSliderAccuracyChartProps) {

	const {
		data,
		xMin,
		xMax,
		yTicks,
		color,
		syncId,
		thresholdValue,
		tooltipTitle,
		chartTitle,
		chartInfoTooltip,
		showDashedLine,
	} = props;

	const { points } = extractMeanAndStdDev(data);
	const filteredPoints = useMemo(() => points.filter((p) => p.x >= xMin && p.x <= xMax), [points, xMin, xMax]);

	const forcedXTicks = useMemo(() => {
		const result: number[] = [];
		const step = 0.1;
		for (let i = xMin; i <= xMax + 1e-8; i += step) {
			result.push(parseFloat(i.toFixed(2)));
		}
		return result;
	}, [xMin, xMax]);

	const clampedThreshold = Math.min(Math.max(thresholdValue ?? xMin, xMin), xMax);
	const tooltipPositionIndex = filteredPoints.findIndex((pt) => Math.abs(pt.x - clampedThreshold) < 1e-8);

	if (!filteredPoints.length) return null;

	return (
		<Box id="accuracy-chart">
			<Box display="flex" justifyContent="center" alignItems="center" mt={1}>
        <Typography variant="subtitle" fontWeight="medium" color={color} mr={1}>
					{chartTitle}
				</Typography>
				<TooltipModalButton
					modalTitle={chartInfoTooltip.title}
					modalContent={chartInfoTooltip.content}
					color="gray-300"
					size="small"
				/>
			</Box>
			{data && (
				<Box ml={-2} pr={1}>
          <ConfidenceLineChart
						data={filteredPoints}
						xTicks={forcedXTicks}
						yTicks={yTicks}
						color={color || "#0288d1"}
						syncId={syncId}
						tooltipTitle={tooltipTitle}
						tooltipPositionIndex={tooltipPositionIndex}
						horizontalLines={5}
						showDashedLine={showDashedLine}
						domain={[xMin, xMax]}
					/>
				</Box>
			)}
			<Divider variant="middle" sx={{ mb: 1 }}>
        <Typography color="text.disabled">
					Confidence Threshold
				</Typography>
			</Divider>
		</Box>
	);
}
